import { ReactNode, useEffect, useRef, useState } from 'react';
import { Spin, Table } from 'antd';
import type { TableProps } from 'antd/es/table';
import './index.css';

type FillTableProps<T extends object> = {
  columns: TableProps<T>['columns'];
  dataSource?: T[];
  rowKey: TableProps<T>['rowKey'];
  loading?: boolean;
  loadingContent?: ReactNode;
  scrollX?: number | string;
  bordered?: boolean;
  locale?: TableProps<T>['locale'];
  className?: string;
};

const defaultLoading = (
  <div className="flex h-full flex-col items-center justify-center gap-3 px-4">
    <Spin size="large" />
    <div className="text-center text-base font-medium text-gray-700">
      Đang tổng hợp dữ liệu báo cáo hệ thống…
    </div>
    <div className="text-center text-sm text-gray-500">
      Hệ thống đang xử lý khối lượng dữ liệu lớn, vui lòng đợi trong giây lát
    </div>
  </div>
);

export function FillTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  loadingContent,
  scrollX = 'max-content',
  bordered = false,
  locale,
  className,
}: FillTableProps<T>) {
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const [tableBodyHeight, setTableBodyHeight] = useState(0);

  useEffect(() => {
    const area = tableAreaRef.current;
    if (!area) return;

    const measure = () => {
      const header = area.querySelector('.ant-table-header') as HTMLElement | null;
      const headerHeight = header?.offsetHeight ?? 0;
      setTableBodyHeight(Math.max(area.clientHeight - headerHeight, 0));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    const header = area.querySelector('.ant-table-header');
    if (header) observer.observe(header);
    return () => observer.disconnect();
  }, [loading, dataSource?.length]);

  return (
    <div ref={tableAreaRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {loading ? (
        loadingContent ?? defaultLoading
      ) : (
        <Table<T>
          className={className ? `fill-table ${className}` : 'fill-table'}
          columns={columns}
          dataSource={dataSource}
          rowKey={rowKey}
          pagination={false}
          size="small"
          bordered={bordered}
          locale={locale}
          scroll={{
            x: scrollX,
            y: tableBodyHeight,
          }}
        />
      )}
    </div>
  );
}
